const EMAIL_REGEX = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

export const normalizeEmail = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim().toLowerCase();
};

export const extractEmailsFromText = (text) => {
  const raw = String(text || "");
  const matches = raw.match(EMAIL_REGEX) || [];
  return matches.map(normalizeEmail).filter(Boolean);
};

export const normalizeWhitespace = (value) => {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim();
};

const cleanNameToken = (value) => {
  // Keep letters/numbers/spaces + parentheses + apostrophes + hyphens
  // (pasted names can include things like "Robert (Scott) Churchward" or "O'Connor")
  return normalizeWhitespace(String(value || "").replace(/[^a-zA-Z0-9()'’\-\s]/g, " "));
};

export const parseNameInput = (raw) => {
  const cleaned = cleanNameToken(raw);
  const parenMatch = cleaned.match(/\(([^)]+)\)/);
  const nickname = parenMatch ? normalizeWhitespace(parenMatch[1]) : "";
  const noParen = normalizeWhitespace(cleaned.replace(/\([^)]*\)/g, " "));
  const parts = noParen.split(" ").filter(Boolean);
  if (parts.length < 2) {
    return { first: "", last: "", nickname: nickname || "" };
  }
  // Support multi-token first names and last-name prefixes/particles.
  // Examples:
  // - "Il Bong Chung" -> first="Il Bong", last="Chung"
  // - "Jessica St. Jean" -> first="Jessica", last="St Jean"
  const lastToken = parts[parts.length - 1];
  const prevToken = parts[parts.length - 2] || "";
  const prevNorm = String(prevToken).toLowerCase().replace(/\./g, "");
  const lastPrefixes = new Set([
    "st",
    "saint",
    "van",
    "von",
    "de",
    "del",
    "di",
    "da",
    "la",
    "le",
    "du",
    // Common particles/prefixes that belong with the last name (e.g. "El Bardaoui")
    "el",
    "al",
    "bin",
    "ibn",
    "bint",
  ]);

  let last = lastToken;
  let firstParts = parts.slice(0, -1);

  if (parts.length >= 3 && lastPrefixes.has(prevNorm)) {
    last = `${prevToken} ${lastToken}`.trim();
    firstParts = parts.slice(0, -2);
  }

  const first = firstParts.join(" ").trim();
  return { first, last, nickname: nickname || "" };
};

export const normalizeNameKey = (first, last) => {
  const f = normalizeWhitespace(first).toLowerCase();
  const l = normalizeWhitespace(last).toLowerCase();
  if (!f || !l) return "";
  return `${f} ${l}`.trim();
};

export const normalizeEmailLocalPartToken = (value) => {
  // Used to derive candidate emails from pasted names (first.last)
  return String(value || "")
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "")
    .trim();
};

const normalizeEmailLocalPartTokenFlexible = (value, { allowHyphen = false, allowDot = false } = {}) => {
  const raw = String(value || "")
    .toLowerCase()
    .replace(/['’]/g, "");

  const disallowed = (() => {
    if (allowHyphen && allowDot) return /[^a-z0-9.-]+/g;
    if (allowHyphen) return /[^a-z0-9-]+/g;
    if (allowDot) return /[^a-z0-9.]+/g;
    return /[^a-z0-9]+/g;
  })();

  let out = raw.replace(disallowed, "");
  if (allowHyphen) out = out.replace(/-+/g, "-");
  if (allowDot) out = out.replace(/\.+/g, ".");
  // local-part cannot start/end with '.' or '-'
  out = out.replace(/^[-.]+|[-.]+$/g, "");
  return out.trim();
};

export const buildEmailLocalPartVariants = (value) => {
  const base = normalizeWhitespace(value).toLowerCase();
  if (!base) return [];

  const variants = [
    // Most common: collapse all separators (existing behavior)
    normalizeEmailLocalPartToken(base),

    // Keep hyphens (for names like "Jean-Luc", "Leroux-Chartre")
    normalizeEmailLocalPartTokenFlexible(base, { allowHyphen: true }),

    // Convert spaces to hyphens (for particles like "El Bardaoui" -> "el-bardaoui")
    normalizeEmailLocalPartTokenFlexible(base.replace(/\s+/g, "-"), { allowHyphen: true }),

    // Convert hyphens to dots (some orgs use jean.luc)
    normalizeEmailLocalPartTokenFlexible(base.replace(/-/g, "."), { allowDot: true }),

    // Convert spaces to dots (e.g. "Mary Jane" -> "mary.jane")
    normalizeEmailLocalPartTokenFlexible(base.replace(/\s+/g, "."), { allowDot: true }),
  ].filter(Boolean);

  return Array.from(new Set(variants));
};

export const buildDerivedEmailLocalParts = ({ firstRaw, lastRaw } = {}) => {
  const firstStr = normalizeWhitespace(firstRaw);
  const lastStr = normalizeWhitespace(lastRaw);
  if (!firstStr || !lastStr) return [];

  // For first names, try both the "first token" and the full first (covers "Jean-Luc" and multi-token first names).
  const firstTokenRaw = firstStr.split(" ")[0] || "";
  const firstVariants = Array.from(
    new Set([
      ...buildEmailLocalPartVariants(firstTokenRaw),
      ...buildEmailLocalPartVariants(firstStr),
    ])
  ).filter(Boolean);

  const lastVariants = buildEmailLocalPartVariants(lastStr).filter(Boolean);
  if (!firstVariants.length || !lastVariants.length) return [];

  const initials = Array.from(
    new Set(firstVariants.map((v) => String(v || "")[0] || "").filter(Boolean))
  );

  const out = [];
  lastVariants.forEach((last) => {
    firstVariants.forEach((first) => {
      out.push(`${first}.${last}`);
      out.push(`${first}${last}`);
    });
    initials.forEach((fi) => {
      out.push(`${fi}.${last}`);
      out.push(`${fi}${last}`);
    });
  });

  return Array.from(new Set(out.filter(Boolean)));
};

export const buildDerivedEmailLocalPartsFromName = (nameRaw) => {
  const cleaned = cleanNameToken(nameRaw);
  if (!cleaned) return [];

  // Strip parentheticals for tokenization, but keep nickname via parseNameInput.
  const noParen = normalizeWhitespace(cleaned.replace(/\([^)]*\)/g, " "));
  const tokens = noParen.split(" ").filter(Boolean);

  const parsed = parseNameInput(nameRaw);
  const basePairs = [
    { firstRaw: parsed.first, lastRaw: parsed.last },
    ...(parsed.nickname ? [{ firstRaw: parsed.nickname, lastRaw: parsed.last }] : []),
  ];

  const extraPairs = [];

  // Heuristic for 3+ token names where email uses `first+last.middle` (e.g. "Joys Kabwa Yuma" -> "joysyuma.kabwa").
  // Use first + last token as the "first" side, and the middle segment as the "last" side.
  if (tokens.length >= 3) {
    const lastPrefixes = new Set([
      "st",
      "saint",
      "van",
      "von",
      "de",
      "del",
      "di",
      "da",
      "la",
      "le",
      "du",
      "el",
      "al",
      "bin",
      "ibn",
      "bint",
    ]);

    const middleFirstTokenNorm = String(tokens[1] || "").toLowerCase().replace(/\./g, "");
    // Avoid adding noisy permutations for known last-name particles (e.g. "Mohamed El Bardaoui").
    if (!lastPrefixes.has(middleFirstTokenNorm)) {
      const altFirst = `${tokens[0]} ${tokens[tokens.length - 1]}`.trim();
      const altLast = tokens.slice(1, -1).join(" ").trim();
      if (altFirst && altLast) {
        extraPairs.push({ firstRaw: altFirst, lastRaw: altLast });
      }
    }
  }

  const allPairs = [...basePairs, ...extraPairs];
  const localParts = allPairs.flatMap((p) => buildDerivedEmailLocalParts(p));
  return Array.from(new Set(localParts.filter(Boolean)));
};

export const extractReportOwnerEmails = (report) => {
  const r = report || {};
  const candidates = [
    r.employee_email,
    r.employee?.email,
    r.created_by_email,
    r.created_by?.email,
    r.owner_email,
    r.owner?.email,
    r.generated_by_email,
    r.generated_by?.email,
    r.generated_by_employee?.email,
  ];
  const emails = candidates.map(normalizeEmail).filter(Boolean);
  return Array.from(new Set(emails));
};

